import React, { useState } from "react";
import { useParams } from "react-router-dom";
import BlockLink from "components/Links/BlockLink/BlockLink";

import { useSpring, a, SpringConfig } from "@react-spring/web";

import { useIntersect } from "hooks/useIntersect";
import { useAnimateInTrail, IAnimateInProps } from "hooks/useAnimateInTrail";
import {
  initialOffset,
  defaultSpringConfig,
} from "components/Animations/SpringProperties/SpringProperties";

import { LazyImage } from "components/Image/Index";

import { useSelector } from "react-redux";
import * as fromProjects from "store/Projects";

type IOwnProps = IAnimateInProps;

type IProps = IOwnProps;

const NextProject: React.FC<IProps> = ({ animationReady, ...props }) => {
  const { projectName } = useParams();

  const [inViewRef, inView] = useIntersect({
    startObserving: animationReady,
  });

  const [animateInTrail] = useAnimateInTrail(inView, 3);
  const nextProjects = useSelector(fromProjects.getNextProjectDetails);

  const getNextProject = () => {
    const { [projectName]: value, ...projectSelection } = nextProjects;

    const numberOfProjects = Object.keys(projectSelection).length;
    const randomProjectIndex = Math.floor(Math.random() * numberOfProjects);

    const randomProject =
      nextProjects[Object.keys(projectSelection)[randomProjectIndex]];

    return randomProject;
  };

  const [project] = useState(getNextProject());

  const tileStyles = {
    opacity: animateInTrail[0].active,
    transform: animateInTrail[0].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const subHeadingStyles = {
    opacity: animateInTrail[1].active,
    transform: animateInTrail[1].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const headingStyles = {
    opacity: animateInTrail[2].active,
    transform: animateInTrail[2].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const hoverSpringConfig: SpringConfig = {
    ...defaultSpringConfig,
    tension: 250,
    friction: 36,
  };

  const [hoverSpring, setHoverSpring] = useSpring(() => ({
    hover: 0,
    config: hoverSpringConfig,
  }));

  const handleMouseEnter = () => {
    setHoverSpring({ hover: 1 });
  };

  const handleMouseLeave = () => {
    setHoverSpring({ hover: 0 });
  };

  const tileBackgroundStyles = {
    width: "100%",
    height: "100%",
    opacity: hoverSpring.hover.to((h) => h * 0.3 + 0.2),
    transform: hoverSpring.hover.to(
      (h) =>
        `translateY(-50%) translateX(-50%) scale(${1 + h * 0.01}, ${
          1 + h * 0.01
        })`
    ),
  };

  const tileShadowStyles = {
    opacity: hoverSpring.hover.to((h) => h * 0.4 + 0.4),
  };

  const messageStyles = {
    opacity: hoverSpring.hover.to((h) => 1 - h),
    transform: hoverSpring.hover.to(
      (h) => `translateY(calc( -50% + (${h} * -30px)))`
    ),
  };

  const titleStyles = {
    opacity: hoverSpring.hover.to((h) => h),
    transform: hoverSpring.hover.to(
      (h) => `translateX(-50%) translateY(calc( -50% + (${h} * -30px) + 30px))`
    ),
  };

  return (
    <section ref={inViewRef} className="nextProjectWrapper">
      <BlockLink
        to={"/work/" + (project != null ? project.linkUrl : null)}
        title={project != null ? project.title : null}
      >
        <a.div
          className="tileWrapper"
          style={tileStyles}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <a.div className="tileBackground" style={tileBackgroundStyles}>
            <LazyImage
              width="100%"
              height="100%"
              src={
                project != null
                  ? "assets/images/work/" + project.nextBackgroundImage
                  : null
              }
              alt="Next project background"
              animateImageIn={true}
              animationType={"fadeIn"}
            />
          </a.div>
          <a.div className="tileShadow" style={tileShadowStyles} />
          <a.div className="messageWrapper" style={messageStyles}>
            <a.h2 style={subHeadingStyles}>Next</a.h2>
            <a.h1 style={headingStyles}>Project</a.h1>
          </a.div>
          <a.div
            className="messageWrapper projectTitleWrapper"
            style={titleStyles}
          >
            <h2>Next Project</h2>
            <h1>{project != null ? project.title : null}</h1>
          </a.div>
        </a.div>
      </BlockLink>
    </section>
  );
};

export default NextProject;
